import {createSelector, MemoizedSelector} from '@ngrx/store';
import {AppState} from '../states/app.state';
import {BelegDTO} from '../../openapi/fakturierung-openapi';
import {fakturierungsbelegEntitiesAdapter} from '../adapters/fakturierungsbeleg-entities.adapter';
import {identity} from 'rxjs';
import {KundeEntitiesSelectors} from './kunde-entities.selectors';


export class FakturierungsbelegEntitiesSelectors {

  private static adapterSelectors = fakturierungsbelegEntitiesAdapter.getSelectors();

  private static select = {
    belegEntities: (state: AppState) => state.fakturierungsbelegEntities,
    totalElements: (state: AppState) => state.fakturierungsbelegEntities.totalElements,
    createSaveActionSuccessful: (state: AppState) => state.fakturierungsbelegEntities.createSaveActionSuccessful,
    deleteActionSuccessful: (state: AppState) => state.fakturierungsbelegEntities.deleteActionSuccessful,
    createIsLoading: (state: AppState) => state.fakturierungsbelegEntities.createIsLoading,
    finalizeIsLoading: (state: AppState) => state.fakturierungsbelegEntities.finalizeIsLoading,
    duplicateIsLoading: (state: AppState) => state.fakturierungsbelegEntities.duplicateIsLoading,
  };

  public static all = createSelector(
    this.select.belegEntities,
    this.adapterSelectors.selectAll,
  );

  public static belegById = (belegId: string) => createSelector(
    this.all,
    belegList => belegList.find(beleg => beleg.id === belegId),
  );

  public static belegeByIds = (belegIds: string[]): MemoizedSelector<AppState, BelegDTO[]> => createSelector(
    this.all,
    belegDtos => {
      const belegMap = new Map(belegDtos.map(belegDto => [belegDto.id, belegDto]));
      return belegIds
        .map(id => belegMap.get(id))
        .filter((belegDto): belegDto is BelegDTO => !!belegDto);
    }
  );

  /**
   * Liefert Anzeige-Referenzen fuer die uebergebenen Beleg-IDs (z.B.
   * verknuepfte Lieferscheine) im Format "NUMMER vom DATUM" — ohne Datum nur
   * die Nummer. Noch nicht geladene Belege und Belege ohne Nummer werden
   * uebersprungen. Die Datumsformatierung liefert der Aufrufer (z.B.
   * DateFormatterService.formatDate), damit der Selector pur bleibt.
   */
  public static lieferscheinReferenzenByIds = (
    belegIds: string[],
    formatDatum: (datum: string) => string,
  ): MemoizedSelector<AppState, string[]> => createSelector(
    this.belegeByIds(belegIds),
    belege => belege
      .filter(beleg => !!beleg.nummer)
      .map(beleg => beleg.datum
        ? `${beleg.nummer} vom ${formatDatum(beleg.datum)}`
        : beleg.nummer as string),
  );

  public static belegeByBetriebId = (betriebId: string) => createSelector(
    this.all,
    belegList => belegList.filter(beleg => beleg && beleg.betriebId === betriebId),
  );

  public static einstellungenById = (belegId: string) => createSelector(
    this.all,
    belegList => belegList.find(beleg => beleg.id === belegId)?.einstellung,
  );

  public static totalElements = createSelector(
    this.select.totalElements,
    identity,
  );

  public static createSaveActionSuccessful = createSelector(
    this.select.createSaveActionSuccessful,
    identity,
  );

  public static deleteActionSuccessful = createSelector(
    this.select.deleteActionSuccessful,
    identity,
  );

  public static createIsLoading = createSelector(
    this.select.createIsLoading,
    identity,
  );

  public static finalizeIsLoading = createSelector(
    this.select.finalizeIsLoading,
    identity,
  );

  public static duplicateIsLoading = createSelector(
    this.select.duplicateIsLoading,
    identity,
  );

  public static problem = (belegId: string) => createSelector(
    this.all,
    belegList => belegList.find(beleg => beleg.id === belegId)?.problem,
  );

  /**
   * Ermittelt den bevorzugten E-Mail-Empfaenger fuer den Versand eines Belegs.
   *
   * Reihenfolge:
   * 1. Empfaenger des neuesten Versandstatus-Eintrags (falls vorhanden)
   * 2. `emailAdresse` der Kundendaten des zugehoerigen Kunden
   * 3. `undefined`
   *
   * @param belegId ID des Belegs
   */
  public static preferredEmpfaengerById = (
    belegId: string,
  ): MemoizedSelector<AppState, string | undefined> => createSelector(
    this.belegById(belegId),
    KundeEntitiesSelectors.all,
    (beleg, kunden) => {
      const letzterEmpfaenger = beleg?.aktuellerVersandstatus?.empfaenger?.trim();
      if (letzterEmpfaenger) {
        return letzterEmpfaenger;
      }
      if (!beleg?.kundeId) {
        return undefined;
      }
      const kunde = kunden.find(k => k.id === beleg.kundeId);
      return kunde?.kundendaten?.emailAdresse?.trim() || undefined;
    },
  );

}
